import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import NotificationsPopover from '@/Modal/NotificationsPopover';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Eye, Plus, X, FileText, MapPin, Clock, Bell, MessageCircle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download, BarChart, Flame, Timer, Target, RefreshCw, Zap, Activity, Dumbbell, Droplet as Droplets, Filter, Link2, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardIncidentTrendWidget from '@/components/DashboardIncidentTrendWidget';
import DashboardAIInsightsWidget from '@/components/DashboardAIInsightsWidget';
import DashboardGeoAnalyticsWidget from '@/components/DashboardGeoAnalyticsWidget';
import DashboardMetricsSection from '@/components/DashboardMetricsSection';
import FilterModal from '@/Modal/FilterModal';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardIncidentModalToggle, setIncidentsDatas, setRecentIncidentHistory } from '@/Redux/Action/organizationActions';
import { CommanServices } from '@/services/commanServices';
import { parseTimestamp } from '@/uitilites/utility';
import socketService from "../services/socketService";
import { isWebAdmin } from '@/auth/webAccess';

const orderTreeItemsDepthFirst = (items) => {
  if (!items.length) return items;

  const byId = new Map(items.map((item) => [item.incident.id, item]));
  const childrenByParent = new Map();

  items.forEach((item) => {
    if (item.parent_id && byId.has(item.parent_id)) {
      if (!childrenByParent.has(item.parent_id)) childrenByParent.set(item.parent_id, []);
      childrenByParent.get(item.parent_id).push(item);
    }
  });

  const roots = items.filter((item) => !item.parent_id || !byId.has(item.parent_id));
  const ordered = [];
  const visited = new Set();

  const walk = (item) => {
    if (visited.has(item.incident.id)) return;
    visited.add(item.incident.id);
    ordered.push(item);
    (childrenByParent.get(item.incident.id) || []).forEach(walk);
  };

  roots.forEach(walk);
  items.forEach((item) => {
    if (!visited.has(item.incident.id)) ordered.push(item);
  });

  return ordered;
};

const buildFilteredIncidentHierarchy = (filtered) => {
  if (!filtered.length) return filtered;

  const displayMap = new Map(filtered.map((incident) => [incident.id, incident]));

  const getDisplayParentId = (incident) => {
    if (incident.parent_incident_id && displayMap.has(incident.parent_incident_id)) {
      return incident.parent_incident_id;
    }
    return null;
  };

  const hasHierarchy = Array.from(displayMap.values()).some((incident) => getDisplayParentId(incident));
  if (!hasHierarchy) return filtered;

  const childrenByParent = new Map();
  displayMap.forEach((incident) => {
    const parentId = getDisplayParentId(incident);
    if (parentId) {
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(incident);
    }
  });

  const roots = Array.from(displayMap.values()).filter((incident) => !getDisplayParentId(incident));
  const ordered = [];

  const appendWithChildren = (incident, depth, parentId) => {
    ordered.push({
      ...incident,
      _treeDepth: depth,
      _parentId: parentId,
    });
    (childrenByParent.get(incident.id) || []).forEach((child) => {
      appendWithChildren(child, depth + 1, incident.id);
    });
  };

  roots.forEach((root) => appendWithChildren(root, 0, null));

  return ordered.length ? ordered : filtered;
};

const Dashboard = () => {
  const { isDashboardIncidentModaltoggle } = useSelector(state => state.organization);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentWebUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch (_) {
      return null;
    }
  }, []);
  const isAdmin = isWebAdmin(currentWebUser);

  const [searchTerm, setSearchTerm] = useState("");
  const [timeFilter, setTimeFilter] = useState("today");
  const [dashboardTitle, setDashboardTitle] = useState(() => {
    return localStorage.getItem('dashboardTitle') || "Dashboard";
  });
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  // Save dashboard title to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dashboardTitle', dashboardTitle);
  }, [dashboardTitle]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userConfigs, setUserConfigs] = useState([]);

  // Simplified Filter State
  const [familyTreeData, setFamilyTreeData] = useState(null); // Store incident family tree data
  const [loadingFamilyTree, setLoadingFamilyTree] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Counter to force refresh

  // Simulation / drill aggregate stats (corrective actions + scenarios)
  const [drillStats, setDrillStats] = useState({ openActions: 0, overdueActions: 0, scenarios: 0 });
  useEffect(() => {
    if (!isAdmin) {
      setDrillStats({ openActions: 0, overdueActions: 0, scenarios: 0 });
      return;
    }
    (async () => {
      try {
        const [ca, sc] = await Promise.all([
          CommanServices.listCorrectiveActions(),
          CommanServices.listScenarios(),
        ]);
        const actions = Array.isArray(ca?.data) ? ca.data : [];
        const now = new Date();
        const open = actions.filter(a => a.status !== 'completed').length;
        const overdue = actions.filter(a => a.status !== 'completed' && a.due_date && new Date(a.due_date) < now).length;
        const scList = sc?.data?.scenarios || (Array.isArray(sc?.data) ? sc.data : []);
        const scenarios = Array.isArray(scList) ? scList.length : (sc?.data?.count || 0);
        setDrillStats({ openActions: open, overdueActions: overdue, scenarios });
      } catch (e) { /* noop */ }
    })();
  }, [isAdmin, refreshTrigger]);
  
  // Active Calls State (incidentId -> callData)
  const [activeCalls, setActiveCalls] = useState({});

  // Simplified Filter State with localStorage persistence
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Initialize filters from localStorage or use defaults
  const [filters, setFilters] = useState(() => {
    try {
      const savedFilters = localStorage.getItem('dashboardFilters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        console.log('Restored filters from localStorage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return {
      location: [],
      level: [],
      status: [],
      dateType: 'all',
      startDate: '',
      endDate: '',
      incidentType: [],
      exerciseType: [],
      event: [],
      trigger: [],
      incidentFamily: '', // Filter by incident family tree
      reportKind: 'all', // 'all' | 'real' | 'drill'
      incidentId: '' // Free-text search by incident id / number
    };
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('dashboardFilters', JSON.stringify(filters));
      console.log('Saved filters to localStorage:', filters);
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  // Check for refresh needed when returning to dashboard
  useEffect(() => {
    const needsRefresh = localStorage.getItem('dashboardNeedsRefresh');
    if (needsRefresh === 'true') {
      console.log('🔄 Returned to dashboard - refresh flag detected, forcing data refresh');
      localStorage.removeItem('dashboardNeedsRefresh');
      fetchDashboardIncidents();
      if (isAdmin) fetchUserConfigs();
      setRefreshTrigger(prev => prev + 1);
    }
  }, [isAdmin]);

  // Incident Summary collapsibility state
  const [isIncidentSummaryCollapsed, setIsIncidentSummaryCollapsed] = useState(false);

  // Column visibility state for Incident Summary with localStorage persistence
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Default visibility (all columns visible)
    const defaults = {
      serial: true,
      incidentId: true,
      dateTime: true,
      incidentType: true,
      exerciseType: true,
      event: true,
      trigger: true,
      location: true,
      department: true,
      duration: true,
      level: true,
      status: true
    };
    // Load saved column preferences from localStorage and merge with defaults
    const saved = localStorage.getItem('incidentSummaryColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        delete parsed.source; // Source column removed
        return { ...defaults, ...parsed, department: parsed.department !== undefined ? parsed.department : true };
      } catch (e) {
        console.error('Error loading column preferences:', e);
      }
    }
    return defaults;
  });

  // Save column visibility preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('incidentSummaryColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Apply column presets when the Report Kind filter changes ('all' keeps the user's columns)
  const prevReportKindRef = useRef(null);
  useEffect(() => {
    const kind = filters.reportKind || 'all';
    if (prevReportKindRef.current === null) { prevReportKindRef.current = kind; return; }
    if (prevReportKindRef.current === kind) return;
    prevReportKindRef.current = kind;
    if (kind === 'real') {
      setVisibleColumns({
        serial: true, incidentId: true, dateTime: true, incidentType: true,
        exerciseType: false, event: false, trigger: false, location: true,
        department: false, duration: true, level: true, status: true
      });
    } else if (kind === 'drill') {
      setVisibleColumns({
        serial: true, incidentId: true, dateTime: true, incidentType: true,
        exerciseType: true, event: false, trigger: false, location: true,
        department: true, duration: true, level: true, status: true
      });
    }
  }, [filters.reportKind]);

  // Store incident classifications for name lookups
  const [incidentClassifications, setIncidentClassifications] = useState([]);
  // Store departments for organizational-scope name lookups
  const [departmentsList, setDepartmentsList] = useState([]);

  // Widget management state
  const DEFAULT_WIDGETS = [
    { id: 'drill-metrics', name: 'Simulation & Drill Metrics', description: 'Exercise readiness & corrective-action overview: drills, actions and scenario library', enabled: true },
    { id: 'incident-metrics', name: 'Incident Metrics', description: 'Real-incident key metrics: total, severity and status breakdown (excludes drills & simulations)', enabled: false },
    { id: 'geo-analytics', name: 'Geospatial Analytics Atlas', description: 'GIS-backed descriptive & analytical KPIs, spatial analysis and incident heatmap', enabled: true },
    { id: 'incident-trend', name: 'Incident Trend', description: 'Time-series visualization of incident volume', enabled: false },
    { id: 'ai-insights', name: 'AI Insights', description: 'AI-powered recommendations and insights', enabled: false }
  ];
  const [availableWidgets, setAvailableWidgets] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('dashboardWidgetState') || 'null');
      // Current format: ordered array of {id, enabled}
      if (Array.isArray(saved)) {
        const byId = Object.fromEntries(DEFAULT_WIDGETS.map(w => [w.id, w]));
        const ordered = saved
          .filter(s => byId[s.id])
          .map(s => ({ ...byId[s.id], enabled: !!s.enabled }));
        // Widgets added to DEFAULT_WIDGETS after the user last saved
        const missing = DEFAULT_WIDGETS.filter(w => !saved.some(s => s.id === w.id));
        return [...ordered, ...missing];
      }
      // Legacy format: {id: enabled} object without ordering
      if (saved && typeof saved === 'object') {
        return DEFAULT_WIDGETS.map(w => ({
          ...w,
          enabled: typeof saved[w.id] === 'boolean' ? saved[w.id] : w.enabled
        }));
      }
      return DEFAULT_WIDGETS;
    } catch (e) {
      return DEFAULT_WIDGETS;
    }
  });
  const [showWidgetModal, setShowWidgetModal] = useState(false);

  // Persist widget order and enabled/disabled state across reloads
  useEffect(() => {
    const state = availableWidgets.map(w => ({ id: w.id, enabled: w.enabled }));
    localStorage.setItem('dashboardWidgetState', JSON.stringify(state));
  }, [availableWidgets]);

  // Widget Management Functions
  const toggleWidget = (widgetId) => {
    setAvailableWidgets(widgets =>
      widgets.map(widget =>
        widget.id === widgetId
          ? { ...widget, enabled: !widget.enabled }
          : widget
      )
    );
  };

  const moveWidget = (widgetId, direction) => {
    setAvailableWidgets(widgets => {
      const index = widgets.findIndex(w => w.id === widgetId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= widgets.length) return widgets;
      const next = [...widgets];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const enabledWidgets = isAdmin
    ? availableWidgets.filter(widget => widget.enabled)
    : DEFAULT_WIDGETS.filter(widget => widget.id === 'incident-metrics');

  // Helper functions (stable, no complex dependencies)
  const getIncidentTypeName = (typeId) => {
    if (!typeId || incidentClassifications.length === 0) return 'Unknown Type';
    const classification = incidentClassifications.find(c => c.id == typeId);
    return classification?.incident_type || 'Unknown Type';
  };

  // Get location display - updated to handle LocationSelector arrays and location_names
  const getLocationDisplay = (incident) => {
    // First check if we have resolved location names from backend
    if (incident?.location_names && Array.isArray(incident.location_names) && incident.location_names.length > 0) {
      return incident.location_names.join(', ');
    }

    // Fallback to location field
    const location = incident?.location || incident;

    // Handle LocationSelector array format (list of location IDs)
    if (Array.isArray(location)) {
      if (location.length === 0) return 'No location specified';
      return `${location.length} location(s) selected`;
    }

    // Handle legacy formats
    if (typeof location === 'string') return location;
    if (location?.display_label) return location.display_label;
    if (location?.location) return location.location;
    return 'No location specified';
  };

  const getEventName = (typeId, eventId) => {
    return eventId && eventId.trim() ? eventId : 'N/A';
  };

  const getTriggerName = (typeId, eventId, triggerId) => {
    return triggerId && triggerId.trim() ? triggerId : 'N/A';
  };

  // Simple active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.location.length > 0 && filters.location[0]) count++;
    if (filters.level.length > 0) count++;
    if (filters.status.length > 0) count++;
    if (filters.dateType !== 'all') count++;
    if (filters.incidentType.length > 0) count++;
    if ((filters.incidentId || '').trim()) count++;
    if ((filters.exerciseType || []).length > 0) count++;
    if (filters.event.length > 0) count++;
    if (filters.trigger.length > 0) count++;
    if (filters.reportKind && filters.reportKind !== 'all') count++;
    return count;
  }, [filters]);

  // Filter handling functions
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      location: [],
      level: [],
      status: [],
      dateType: 'all',
      startDate: '',
      endDate: '',
      incidentType: [],
      exerciseType: [],
      event: [],
      trigger: [],
      incidentFamily: '',
      reportKind: 'all',
      incidentId: ''
    });
  };

  const handleApplyFilters = () => {
    setShowFilterModal(false);
  };

  // Handle new incident creation
  const handleIncidentCreated = (newIncident) => {
    console.log('New incident created:', newIncident);
    // Refresh incidents data
    fetchDashboardIncidents();
  };

  // Fetch incident classifications for name lookups
  const fetchIncidentClassifications = async () => {
    try {
      const response = await CommanServices.incidentClassifications();
      if (response.status) {
        const data = response.data;
        setIncidentClassifications(data);
      }
    } catch (error) {
      console.error('Error fetching incident classifications:', error);
      setIncidentClassifications([]);
    }
  };

  const fetchDepartmentsList = async () => {
    try {
      const response = await CommanServices.departments();
      if (response.status) {
        const data = await response.data;
        setDepartmentsList(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartmentsList([]);
    }
  };

  // Resolve a list of department ids into a readable names string
  const getDepartmentNames = (deptIds) => {
    if (!Array.isArray(deptIds) || deptIds.length === 0) return '';
    return deptIds
      .map((id) => {
        const d = departmentsList.find((x) => x.id === id);
        return d ? (d.department_name || d.name) : null;
      })
      .filter(Boolean)
      .join(', ');
  };

  // Fetch all user configurations for resolving incident source roles
  const fetchUserConfigs = async () => {
    try {
      const response = await CommanServices.getuserconfigs();
      if (response.status) {
        setUserConfigs(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching user configs in Dashboard:', error);
    }
  };

  // State for total incident count (for pagination)
  const [totalIncidentCount, setTotalIncidentCount] = useState(0);

  // Fetch real incidents from backend with improved server time sync and pagination
  const fetchDashboardIncidents = async (pageNum = 1, pageSize = 50) => {
    try {
      setLoading(true);

      // Calculate skip value for pagination
      const skip = (pageNum - 1) * pageSize;

      // Add timestamp to request to measure network latency
      const requestStart = Date.now();

      // For now, let's get ALL incidents by using a high limit to see total count
      // In production, you'd implement proper pagination with total count from backend
      await fetchIncidentClassifications();
      const response = await CommanServices.getIncidentsData();
      if (response.status) {
        // Store total count and current page incidents
        dispatch(setIncidentsDatas(response.data));
        setTotalIncidentCount(response.data.length);
        setIncidents(response.data);
      }

    } catch (error) {
      console.error('Error fetching dashboard incidents:', error);
      // Reset server time offset on error
      window.serverTimeOffset = 0;
      setIncidents([]);
      setTotalIncidentCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all active calls for the organization
  const fetchActiveCalls = async () => {
    try {
      const response = await CommanServices.getActiveCalls();
      if (response.status && response.data) {
        const calls = Array.isArray(response.data) ? response.data : [response.data];
        const callMap = {};
        calls.forEach(call => {
          if (call && call.incidentId) {
            callMap[call.incidentId] = call;
          }
        });
        setActiveCalls(callMap);
      }
    } catch (error) {
      console.error('Error fetching active calls:', error);
    }
  };

  useEffect(() => {
    fetchDashboardIncidents();
    fetchIncidentClassifications();
    if (isAdmin) fetchDepartmentsList();
    fetchActiveCalls();
    if (isAdmin) fetchUserConfigs();

    // Check if dashboard needs refresh (flag set when related incident created)
    const needsRefresh = localStorage.getItem('dashboardNeedsRefresh');
    if (needsRefresh === 'true') {
      console.log('🔄 Dashboard needs refresh flag detected - forcing refresh');
      localStorage.removeItem('dashboardNeedsRefresh');
      setRefreshTrigger(prev => prev + 1);
    }

    // Set up real-time polling for Live Line updates (60s fallback)
    const pollingInterval = setInterval(() => {
      fetchDashboardIncidents();
      fetchActiveCalls();
      if (isAdmin) fetchUserConfigs();
    }, 60000); // Poll every 60 seconds

    // Socket listeners for real-time call updates
    const unsubscribeNewCall = socketService.on('new_call', (data) => {
      if (data && data.incidentId) {
        setActiveCalls(prev => ({
          ...prev,
          [data.incidentId]: data
        }));
      }
    });

    const unsubscribeStatus = socketService.on('call_status_update', (data) => {
      if (data && data.incidentId) {
        if (data.status === 'active' || data.status === 'ringing') {
          setActiveCalls(prev => ({
            ...prev,
            [data.incidentId]: data
          }));
        } else {
          setActiveCalls(prev => {
            const next = { ...prev };
            delete next[data.incidentId];
            return next;
          });
        }
      }
    });

    // Listen for dashboard refresh events (triggered when related incidents are created)
    const handleDashboardRefresh = () => {
      console.log('🔄 Dashboard refresh triggered - refreshing incidents and family tree');
      fetchDashboardIncidents();
      if (isAdmin) fetchUserConfigs();
      setRefreshTrigger(prev => prev + 1); // Increment to trigger family tree refresh
    };

    window.addEventListener('dashboardRefresh', handleDashboardRefresh);

    return () => {
      clearInterval(pollingInterval);
      window.removeEventListener('dashboardRefresh', handleDashboardRefresh);
      unsubscribeNewCall();
      unsubscribeStatus();
    };
  }, [isAdmin]);

  // Fetch incident family tree when family filter is active
  useEffect(() => {
    const fetchFamilyTree = async (forceRefresh = false) => {
      if (!filters.incidentFamily) {
        setFamilyTreeData(null);
        return;
      }

      try {
        setLoadingFamilyTree(true);
        // Add timestamp to prevent caching
        const timestamp = forceRefresh ? `&t=${Date.now()}` : '';
        const response = await CommanServices.getincidentsFamily(filters.incidentFamily);

        if (response.status) {
          const data = await response.data;
          console.log('Family tree data loaded:', data.total_count, 'incidents');
          setFamilyTreeData(data);
        } else {
          console.error('Failed to fetch family tree:', response.status);
          setFamilyTreeData(null);
        }
      } catch (error) {
        console.error('Error fetching family tree:', error);
        setFamilyTreeData(null);
      } finally {
        setLoadingFamilyTree(false);
      }
    };

    // Initial fetch
    fetchFamilyTree();

    // Also listen for dashboard refresh events to update family tree
    const handleRefresh = () => {
      if (filters.incidentFamily) {
        console.log('🔄 Refreshing family tree due to dashboard refresh event');
        fetchFamilyTree(true); // Force refresh
      }
    };

    window.addEventListener('dashboardRefresh', handleRefresh);

    return () => {
      window.removeEventListener('dashboardRefresh', handleRefresh);
    };
  }, [filters.incidentFamily, incidents, refreshTrigger]);


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; // Show 50 incidents per page

  // Simple filtered incidents calculation
  const filteredIncidents = useMemo(() => {
    // If incident family filter is active, use family tree data (overrides all other filters)
    if (filters.incidentFamily && familyTreeData) {
      const visibleFamilyTree = (familyTreeData.family_tree || [])
        .filter((item) => item?.incident?.is_planned_event !== true);
      const visibleFamilyIds = new Set(visibleFamilyTree.map((item) => item?.incident?.id));
      const normalizedFamilyTree = visibleFamilyTree.map((item) => (
        item?.parent_id && !visibleFamilyIds.has(item.parent_id)
          ? { ...item, depth: 0, parent_id: null }
          : item
      ));
      return orderTreeItemsDepthFirst(normalizedFamilyTree)
        .map((item) => ({
          ...item.incident,
          _treeDepth: item.depth,
          _parentId: item.parent_id,
        }));
    }

    const incidentsArray = Array.isArray(incidents) ? incidents : [];
    let filtered = [...incidentsArray];

    // Real incident vs drill vs SOS filter (saved filters may predate this key)
    const reportKind = filters.reportKind || 'all';
    if (reportKind === 'real') {
      filtered = filtered.filter(incident => !incident.is_exercise && !incident.is_scenario);
    } else if (reportKind === 'drill') {
      filtered = filtered.filter(incident => incident.is_exercise);
    } else if (reportKind === 'sos') {
      // Both true SOS incidents and SOS that an admin re-classified — the
      // created_as_sos origin marker survives re-classification.
      filtered = filtered.filter(incident =>
        incident.created_as_sos === true ||
        String(incident.incident_type || '').toUpperCase() === 'SOS'
      );
    }

    // Location filter with LocationSelector array matching
    if (filters.location.length > 0) {
      filtered = filtered.filter(incident => {
        if (!incident.location && (!incident.location_names || incident.location_names.length === 0)) {
          return false;
        }

        // Check if any of the selected location IDs match the incident's location
        return filters.location.some(selectedLocationId => {
          // First check direct location array match (incident.location is array of IDs)
          if (Array.isArray(incident.location)) {
            return incident.location.includes(selectedLocationId);
          }

          // Also check location_names for partial matching (for display purposes)
          if (incident.location_names && Array.isArray(incident.location_names)) {
            // This is a fallback for incidents where we have resolved location names
            // We'll need to match by name components since we have location IDs in filter
            return incident.location_names.some(locationName => {
              // This is a simple contains check - in production you might want more sophisticated matching
              return locationName.toLowerCase().includes(selectedLocationId.toLowerCase());
            });
          }

          return false;
        });
      });
    }

    // Incident Type filter
    if (filters.incidentType.length > 0) {
      filtered = filtered.filter(incident => {
        const incidentType = getIncidentTypeName(incident.type_id);
        return filters.incidentType.some(selectedType =>
          incidentType.toLowerCase().includes(selectedType.toLowerCase())
        );
      });
    }

    // Incident ID search (id, incident number, or code — partial match)
    if ((filters.incidentId || '').trim()) {
      const q = filters.incidentId.trim().toLowerCase();
      filtered = filtered.filter(incident =>
        String(incident.id || '').toLowerCase().includes(q) ||
        String(incident.incident_number || '').toLowerCase().includes(q) ||
        String(incident.code || '').toLowerCase().includes(q)
      );
    }

    // Exercise Type (Exercise Classification) filter
    if ((filters.exerciseType || []).length > 0) {
      filtered = filtered.filter(incident => {
        const cls = incident.exercise_classification || '';
        return filters.exerciseType.includes(cls);
      });
    }

    // Event filter
    if (filters.event.length > 0) {
      filtered = filtered.filter(incident => {
        const eventName = getEventName(incident.type_id, incident.event_id);
        return filters.event.some(selectedEvent =>
          eventName !== 'N/A' && eventName.toLowerCase().includes(selectedEvent.toLowerCase())
        );
      });
    }

    // Trigger filter
    if (filters.trigger.length > 0) {
      filtered = filtered.filter(incident => {
        const triggerName = getTriggerName(incident.type_id, incident.event_id, incident.trigger_id);
        return filters.trigger.some(selectedTrigger =>
          triggerName !== 'N/A' && triggerName.toLowerCase().includes(selectedTrigger.toLowerCase())
        );
      });
    }

    // Level filter
    if (filters.level.length > 0) {
      filtered = filtered.filter(incident =>
        filters.level.includes(incident.severity)
      );
    }

    // Status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(incident =>
        filters.status.includes(incident.status)
      );
    }

    // Date filter
    if (filters.dateType !== 'all') {
      const now = new Date();
      let startDate, endDate;

      switch (filters.dateType) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case 'week':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          endDate = now;
          break;
        case 'month':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          endDate = now;
          break;
        case 'custom':
          if (filters.startDate) startDate = new Date(filters.startDate);
          if (filters.endDate) endDate = new Date(filters.endDate);
          break;
      }

      if (startDate || endDate) {
        filtered = filtered.filter(incident => {
          const incidentDate = new Date(incident.detected_at || incident.created_at);
          if (startDate && incidentDate < startDate) return false;
          if (endDate && incidentDate > endDate) return false;
          return true;
        });
      }
    }

    const hasDashboardFilters =
      filters.location.length > 0 ||
      filters.level.length > 0 ||
      filters.status.length > 0 ||
      filters.dateType !== 'all' ||
      filters.incidentType.length > 0 ||
      (filters.exerciseType || []).length > 0 ||
      filters.event.length > 0 ||
      filters.trigger.length > 0 ||
      (filters.reportKind && filters.reportKind !== 'all');

    if (hasDashboardFilters) {
      return buildFilteredIncidentHierarchy(filtered);
    }

    return filtered;
  }, [incidents, filters, incidentClassifications, familyTreeData]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredIncidents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedIncidents = filteredIncidents.slice(startIndex, endIndex);

  // Pagination is working correctly

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // All filter functions are now handled directly by MultiSelectDropdown onChange callbacks

  // All filter helper functions replaced with memoized filterOptions

  // Filters are now applied automatically via useMemo
  const displayIncidents = paginatedIncidents;

  const openDashboardDrills = useCallback((status = null) => {
    setFilters((current) => ({
      ...current,
      reportKind: 'drill',
      status: status ? [status] : [],
      incidentFamily: '',
    }));
  }, []);

  // Memoize metrics calculations to prevent unnecessary re-renders.
  // Drill metrics intentionally use the complete incident set so a saved
  // real-incident filter does not hide the drill portfolio.
  const { lowIncidents, mediumIncidents, highIncidents, totalIncidents, keyMetrics } = useMemo(() => {
    const low = filteredIncidents.filter(i => i.severity === 'Low').length;
    const medium = filteredIncidents.filter(i => i.severity === 'Medium').length;
    const high = filteredIncidents.filter(i => i.severity === 'High').length;
    const total = filteredIncidents.length;

    // Calculate status-based counts
    const openCount = filteredIncidents.filter(i => i.status === 'open').length;
    const inProgressCount = filteredIncidents.filter(i => i.status === 'in_progress').length;
    const resolvedCount = filteredIncidents.filter(i => i.status === 'resolved').length;
    const relatedCount = filteredIncidents.filter(i => i.parent_incident_id).length; // Incidents that have a parent

    // Simulation / drill specific counts
    const exercises = (Array.isArray(incidents) ? incidents : []).filter(i => i.is_exercise);
    const exTotal = exercises.length;
    const exRequested = exercises.filter(i => i.status === 'requested').length;
    const exActive = exercises.filter(i => i.status === 'active').length;
    const exCompleted = exercises.filter(i => i.status === 'completed').length;
    const exResolved = exercises.filter(i => i.status === 'resolved').length;

    const metrics = [
      { id: 'all-drills', title: "Total Drills", value: String(exTotal ?? 0), icon: Flame, onClick: () => openDashboardDrills() },
      { id: 'requested-drills', title: "Requested", value: String(exRequested ?? 0), color: "bg-amber-500", icon: null, onClick: () => openDashboardDrills('requested') },
      { id: 'active-drills', title: "Active", value: String(exActive ?? 0), color: "bg-blue-500", icon: null, onClick: () => openDashboardDrills('active') },
      { id: 'completed-drills', title: "Completed", value: String(exCompleted ?? 0), color: "bg-emerald-500", icon: null, onClick: () => openDashboardDrills('completed') },
      { id: 'resolved-drills', title: "Resolved", value: String(exResolved ?? 0), color: "bg-green-600", icon: null, onClick: () => openDashboardDrills('resolved') },
      { id: 'open-actions', title: "Open Actions", value: String(drillStats?.openActions ?? 0), color: "bg-indigo-500", icon: null },
      { id: 'overdue-actions', title: "Overdue Actions", value: String(drillStats?.overdueActions ?? 0), color: "bg-rose-500", icon: null },
      { id: 'scenario-library', title: "Scenario Library", value: String(drillStats?.scenarios ?? 0), icon: null, onClick: () => navigate('/scenario-library') }
    ];

    return {
      lowIncidents: low,
      mediumIncidents: medium,
      highIncidents: high,
      totalIncidents: total,
      keyMetrics: metrics
    };
  }, [filteredIncidents, incidents, drillStats, navigate, openDashboardDrills]);

  // Real-incident key metrics for the optional "Incident Metrics" widget
  // (pre-fork DisasterMS dashboard cards; drills and scenario incidents excluded)
  const incidentKeyMetrics = useMemo(() => {
    const real = filteredIncidents.filter(i => !i.is_exercise && !i.is_scenario);
    const count = (pred) => real.filter(pred).length;
    return [
      { title: "Total Incidents", value: String(real.length) },
      { title: "Low Incidents", value: String(count(i => i.severity === 'Low')), color: "bg-yellow-500" },
      { title: "Medium Incidents", value: String(count(i => i.severity === 'Medium')), color: "bg-orange-500" },
      { title: "High Incidents", value: String(count(i => i.severity === 'High')), color: "bg-red-500" },
      { title: "Open Incidents", value: String(count(i => i.status === 'open')), color: "bg-red-500" },
      { title: "In-Progress Incidents", value: String(count(i => i.status === 'in_progress')), color: "bg-yellow-500" },
      { title: "Resolved Incidents", value: String(count(i => i.status === 'resolved')), color: "bg-green-500" },
      { title: "Related Incidents", value: String(count(i => i.parent_incident_id)) }
    ];
  }, [filteredIncidents]);

  // Calculate real notification count based on urgent incidents
  const notificationCount = useMemo(() => {
    const incidentsArray = Array.isArray(incidents) ? incidents : [];

    // Count notifications as:
    // 1. High severity open incidents
    // 2. Recent incidents (created in last 2 hours)
    // 3. Medium severity incidents that are less than 30 minutes old

    const now = new Date();
    let count = 0;

    incidentsArray.forEach(incident => {
      const isOpen = incident.status === 'open';
      const isHigh = incident.severity === 'High';
      const isMedium = incident.severity === 'Medium';

      // Parse incident creation time
      const createdAt = new Date(incident.created_at || incident.detected_at);
      const ageInMinutes = (now - createdAt) / (1000 * 60);
      const ageInHours = ageInMinutes / 60;

      // Count as notification if:
      if (isOpen && isHigh) {
        count++; // All open high severity incidents
      } else if (isOpen && isMedium && ageInMinutes <= 30) {
        count++; // Recent medium severity incidents (30 min)
      } else if (isOpen && ageInHours <= 2) {
        count++; // Any open incident in last 2 hours
      }
    });

    return Math.min(count, 99); // Cap at 99 for display purposes
  }, [incidents]);

  // Helper function to calculate duration according to specifications
  const calculateDuration = (createdAt, resolvedAt, status) => {
    const startTime = parseTimestamp(createdAt);
    const endTime = (status === 'resolved' && resolvedAt)
      ? parseTimestamp(resolvedAt)
      : new Date();

    if (!startTime || isNaN(startTime.getTime()) || !endTime || isNaN(endTime.getTime())) {
      return '0m';
    }

    const diffMs = endTime - startTime;

    if (diffMs < 0) return '0m';

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const calculateIncidentDuration = (incident) => {
    const normalizedStatus = String(incident?.status || 'open').toLowerCase();

    if (incident?.is_exercise) {
      if (normalizedStatus === 'requested') {
        if (incident.planned_start_at && incident.planned_end_at) {
          return `${calculateDuration(
            incident.planned_start_at,
            incident.planned_end_at,
            'resolved'
          )} planned`;
        }
        return 'Not started';
      }

      const startedAt = incident.activated_at
        || incident.planned_start_at
        || incident.detected_at
        || incident.created_at;
      const finishedAt = incident.completed_at || incident.resolved_at;

      return calculateDuration(
        startedAt,
        finishedAt,
        finishedAt ? 'resolved' : normalizedStatus
      );
    }

    return calculateDuration(
      incident?.detected_at || incident?.created_at,
      incident?.resolved_at,
      normalizedStatus
    );
  };

  // Auto-update timer for open incidents duration and Live Line timestamps
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Get recent incidents for Live Line (same data as Incident Summary) - MOVED AFTER updateTrigger declaration
  const liveIncidents = useMemo(() => {
    const incidentsArray = Array.isArray(incidents) ? incidents : [];

    if (incidentsArray.length === 0) {
      // Fallback message when no incidents are available
      return [{
        id: 'no-incidents',
        name: 'System Status',
        event: '',
        location: 'All Systems Operational',
        severity: 'Low',
        created_at: new Date().toISOString(),
        status: 'monitoring'
      }];
    }

    // Sort by created_at descending to get newest incidents first (same ordering as Incident Summary)
    const sortedIncidents = [...incidentsArray].sort((a, b) => {
      const dateA = new Date(a.created_at || a.detected_at);
      const dateB = new Date(b.created_at || b.detected_at);
      return dateB - dateA; // Newest first - matches Incident Summary table ordering
    });

    // Take the 8 most recent incidents for continuous scroll
    const recentIncidents = sortedIncidents.slice(0, 8);

    // Transform to Live Line format with real incident data
    return recentIncidents.map(incident => ({
      id: incident.id,
      name: getIncidentTypeName(incident.type_id),
      event: getEventName(incident.type_id, incident.event_id),
      location: getLocationDisplay(incident),
      severity: incident.severity || 'Medium',
      created_at: incident.created_at || incident.detected_at,
      status: incident.status,
      is_exercise: incident.is_exercise,
      planned_start_at: incident.planned_start_at,
      // Countdown to the planned Start Time for upcoming exercises (e.g. "Starts in 3d 4h")
      startsIn: (() => {
        if (!incident.is_exercise || !incident.planned_start_at) return null;
        const start = new Date(incident.planned_start_at).getTime();
        if (Number.isNaN(start)) return null;
        const diff = start - Date.now();
        if (diff <= 0) return null; // already started/past — fall back to "ago"
        const mins = Math.floor(diff / 60000);
        const days = Math.floor(mins / 1440);
        const hours = Math.floor((mins % 1440) / 60);
        const minutes = mins % 60;
        if (days > 0) return `Starts in ${days}d ${hours}h`;
        if (hours > 0) return `Starts in ${hours}h ${minutes}m`;
        if (minutes > 0) return `Starts in ${minutes}m`;
        return 'Starting now';
      })(),
      // Use same duration calculation as Incident Summary table, formatted as "ago"
      timeAgo: (() => {
        try {
          const duration = calculateIncidentDuration(incident);

          // Convert duration format "Xh Ym" or "Xm" to "Xh Ym ago" format
          if (duration === '0m') return 'Just now';
          if (duration === 'Not started' || duration.endsWith(' planned')) return duration;

          return `${duration} ago`;
        } catch (error) {
          console.error('Error calculating timeAgo for Live Line:', error, 'for incident:', incident.id);
          return 'Unknown';
        }
      })()
    }));
  }, [incidents, incidentClassifications, updateTrigger]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Update Live Line timestamps and incident durations every 30 seconds for accuracy
      setUpdateTrigger(prev => prev + 1);
    }, 30000); // Update every 30 seconds for more accurate timestamps

    return () => clearInterval(interval);
  }, []);

  // Memoize user configurations role lookup map
  const userRoleMap = useMemo(() => {
    const map = new Map();
    userConfigs.forEach(u => {
      if (u.id) map.set(String(u.id).toLowerCase(), u.role);
      if (u.user_account) map.set(String(u.user_account).toLowerCase(), u.role);
    });
    return map;
  }, [userConfigs]);

  // Determine incident source based on creator's role
  const getIncidentSource = (incident) => {
    if (!incident) return 'App User';
    
    const creatorId = incident.created_by;
    if (creatorId) {
      const role = userRoleMap.get(String(creatorId).toLowerCase());
      if (role === 'admin' || role === 'superadmin') {
        return 'Dispatch';
      }
    }
    
    const creatorName = incident.created_by_name || incident.reporter_name;
    if (creatorName) {
      const role = userRoleMap.get(String(creatorName).toLowerCase());
      if (role === 'admin' || role === 'superadmin') {
        return 'Dispatch';
      }
    }
    
    return 'App User';
  };

  // Memoize processed incidents to prevent unnecessary re-renders
  const processedIncidents = useMemo(() => {
    const statusColors = {
      'open': 'bg-red-600',
      'in_progress': 'bg-yellow-500',
      'resolved': 'bg-green-600',
      'requested': 'bg-violet-600',
      'active': 'bg-indigo-600',
      'completed': 'bg-emerald-600'
    };

    const levelColors = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-orange-100 text-orange-800',
      'Low': 'bg-yellow-100 text-yellow-800'
    };

    return displayIncidents.map(incident => {
      // False Alarm is a verification result, not an incident lifecycle state.
      // Older records may still contain the retired value, so present them as
      // open and keep their duration running until an administrator resolves.
      const normalizedStatus = String(incident.status || 'open').toLowerCase() === 'false_alarm'
        ? 'open'
        : String(incident.status || 'open');
      return {
      id: incident.id || 'unknown',
      incidentType: getIncidentTypeName(incident.type_id),
      exerciseType: incident.exercise_classification || '—',
      event: getEventName(incident.type_id, incident.event_id),
      trigger: getTriggerName(incident.type_id, incident.event_id, incident.trigger_id),
      location: getLocationDisplay(incident),
      locationVerified: Boolean(incident?.map_projection?.official),
      department: getDepartmentNames(incident.impacted_departments),
      duration: calculateIncidentDuration({ ...incident, status: normalizedStatus }),
      source: getIncidentSource(incident),
      level: String(incident.resolved_severity || incident.severity || 'Low'),
      status: normalizedStatus,
      statusColor: statusColors[normalizedStatus] || 'bg-gray-600',
      levelColor: levelColors[incident.resolved_severity || incident.severity] || 'bg-gray-100 text-gray-800',
      // Add tooltip info for resolved severity
      resolvedFrom: incident.resolved_from,
      // Include date fields for table display
      created_at: incident.created_at,
      detected_at: incident.detected_at,
      updated_at: incident.updated_at,
      resolved_at: incident.resolved_at,
      // Include parent_incident_id for related incident tracking
      parent_incident_id: incident.parent_incident_id,
      // Include tree structure info for family filter
      _treeDepth: incident._treeDepth,
      _parentId: incident._parentId,
      is_exercise: incident.is_exercise
    };
    });
  }, [displayIncidents, updateTrigger, incidentClassifications, departmentsList]);

  // Structured compass data - following incident classification hierarchy
  const compassSectors = [
    {
      id: "fire",
      name: "Fire",
      icon: Flame,
      angle: 0, // North
      color: "bg-red-500",
      textColor: "text-red-700",
      count: 3,
      severity: "High",
      events: [
        { name: "Temperature Rise", triggers: ["Threshold: 85°C", "Rate: 5°C/min"], count: 2, severity: "High" },
        { name: "Smoke Detection", triggers: ["Optical Sensor", "Ionization Sensor"], count: 1, severity: "Medium" }
      ]
    },
    {
      id: "power",
      name: "Power Failure",
      icon: Zap,
      angle: 90, // East
      color: "bg-blue-600",
      textColor: "text-blue-700",
      count: 2,
      severity: "Medium",
      events: [
        { name: "Fuel Level", triggers: ["Low: <20%", "Critical: <5%"], count: 1, severity: "Medium" },
        { name: "Generator Status", triggers: ["Offline", "Maintenance"], count: 1, severity: "Low" }
      ]
    },
    {
      id: "medical",
      name: "Medical Gas",
      icon: Activity,
      angle: 180, // South  
      color: "bg-red-600",
      textColor: "text-red-700",
      count: 4,
      severity: "High",
      events: [
        { name: "Pressure Drop", triggers: ["Below 50 PSI", "Rate >10 PSI/min"], count: 3, severity: "High" },
        { name: "Gas Leak", triggers: ["Sensor Alert", "Manual Report"], count: 1, severity: "Medium" }
      ]
    },
    {
      id: "flood",
      name: "Flood Risk",
      icon: Droplets,
      angle: 270, // West
      color: "bg-blue-800",
      textColor: "text-blue-700",
      count: 1,
      severity: "Low",
      events: [
        { name: "Water Level", triggers: ["High: >2ft", "Rising: 6in/hr"], count: 1, severity: "Low" }
      ]
    }
  ];

  // Find highest priority incident for needle direction
  const getMostCriticalSector = () => {
    const priority = { "High": 3, "Medium": 2, "Low": 1 };
    return compassSectors.reduce((prev, current) =>
      priority[current.severity] > priority[prev.severity] ? current : prev
    );
  };

  const StructuredIncidentCompass = () => {
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drillMode, setDrillMode] = useState('incidents'); // 'incidents', 'events', 'triggers'
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredSector, setHoveredSector] = useState(null);

    const mostCritical = getMostCriticalSector();

    const handleIncidentClick = (sector) => {
      setSelectedIncident(sector);
      setSelectedEvent(null);
      setDrillMode('events');
    };

    const handleEventClick = (event) => {
      setSelectedEvent(event);
      setDrillMode('triggers');
    };

    const handleBack = () => {
      if (drillMode === 'triggers') {
        setSelectedEvent(null);
        setDrillMode('events');
      } else if (drillMode === 'events') {
        setSelectedIncident(null);
        setDrillMode('incidents');
      }
    };

    const getSeverityColor = (severity) => {
      switch (severity) {
        case "High": return "bg-red-100 text-red-800 border-red-300";
        case "Medium": return "bg-orange-100 text-orange-800 border-orange-300";
        case "Low": return "bg-yellow-100 text-yellow-800 border-yellow-300";
        default: return "bg-gray-100 text-gray-800";
      }
    };

    return (
      <div className="relative bg-white/80 backdrop-blur-lg border border-white/30 rounded-2xl shadow-2xl overflow-hidden">
        {/* Glassmorphism background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-blue-50/30 to-indigo-100/20"></div>

        {/* Drill-down Header - Only for non-main compass view */}
        {drillMode !== 'incidents' && (
          <div className="relative z-10 p-4 text-center border-b border-white/20">
            <div className="flex items-center justify-center space-x-3">
              <Button
                size="sm"
                variant="outline"
                onClick={handleBack}
                className="bg-white/80 border-gray-200 hover:bg-white h-8 w-8 p-0 rounded-full shadow-md"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <p className="text-gray-800 font-semibold">
                {drillMode === 'events' ? `${selectedIncident?.name} Events` :
                  `${selectedEvent?.name} Triggers`}
              </p>
            </div>
          </div>
        )}

        {/* Main Redesigned Compass - No Header */}
        {drillMode === 'incidents' && (
          <div className="relative p-6">
            <div
              className="relative w-64 h-64 mx-auto"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => {
                setIsHovered(false); 8
                setHoveredSector(null);
              }}
            >
              {/* 3D Compass Base with Glassmorphism */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-100/60 via-blue-50/40 to-indigo-100/30 rounded-full border-4 border-white/50 shadow-2xl backdrop-blur-sm">
                <div className="absolute inset-2 bg-gradient-to-br from-white/20 to-transparent rounded-full"></div>
              </div>

              {/* Compass Grid Lines */}
              <div className="absolute inset-0">
                <div className="absolute top-1/2 left-4 right-4 h-px bg-gradient-to-r from-transparent via-gray-400/60 to-transparent"></div>
                <div className="absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-gray-400/60 to-transparent"></div>
              </div>

              {/* Incident Type Icons Around Compass */}
              <div className="absolute inset-0">
                {compassSectors.map((sector, index) => {
                  const isActive = sector.id === mostCritical.id;
                  const isHovering = hoveredSector === sector.id;

                  // Calculate position around circle
                  const angle = sector.angle;
                  const radius = 110; // Distance from center
                  const x = Math.cos((angle - 90) * Math.PI / 180) * radius;
                  const y = Math.sin((angle - 90) * Math.PI / 180) * radius;

                  return (
                    <div
                      key={sector.id}
                      className="absolute cursor-pointer group transform transition-all duration-300"
                      style={{
                        left: `calc(50% + ${x}px - 24px)`,
                        top: `calc(50% + ${y}px - 24px)`,
                      }}
                      onClick={() => handleIncidentClick(sector)}
                      onMouseEnter={() => setHoveredSector(sector.id)}
                      onMouseLeave={() => setHoveredSector(null)}
                      title={`${sector.name}: ${sector.count} incidents (${sector.severity} priority)`}
                    >
                      {/* Icon with 3D Effect */}
                      <div className={`relative w-12 h-12 ${sector.color} rounded-full flex items-center justify-center shadow-xl transform transition-all duration-300 border-2 border-white/80 ${isHovering ? 'scale-125 shadow-2xl' : ''
                        } ${isActive ? 'ring-4 ring-yellow-400/80 animate-pulse' : ''}`}>
                        <sector.icon className="h-6 w-6 text-white drop-shadow-lg" />

                        {/* Circular Incident Count Bubble */}
                        <div className={`absolute -top-1 -right-1 min-w-[20px] h-5 ${sector.severity === 'High' ? 'bg-red-500' :
                            sector.severity === 'Medium' ? 'bg-orange-500' : 'bg-yellow-500'
                          } text-white rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white`}>
                          {sector.count}
                        </div>

                        {/* Glassmorphism overlay for 3D effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent rounded-full"></div>
                      </div>

                      {/* Enhanced Hover Tooltip */}
                      {isHovering && (
                        <div className="absolute z-20 bg-gray-900/95 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-xs font-medium shadow-2xl border border-gray-700 whitespace-nowrap transform -translate-x-1/2 -translate-y-full mt-2 left-1/2 top-0">
                          <div className="font-semibold">{sector.name}</div>
                          <div className="text-gray-300">{sector.count} incidents • {sector.severity}</div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900/95"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Bold High-Contrast Arrow Pointer */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-2 h-28 origin-bottom transform transition-transform duration-1000 ease-out shadow-lg"
                  style={{
                    background: 'linear-gradient(to top, #DC2626, #991B1B, #7F1D1D)',
                    transform: `rotate(${mostCritical.angle - 90}deg)`,
                    clipPath: 'polygon(40% 0%, 60% 0%, 100% 100%, 50% 85%, 0% 100%)'
                  }}
                />

                {/* Center Hub */}
                <div className="absolute w-6 h-6 bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-full border-3 border-white shadow-xl">
                  <div className="absolute inset-0.5 bg-gradient-to-br from-white/20 to-transparent rounded-full"></div>
                </div>
              </div>

              {/* Cardinal Direction Labels */}
              <div className="absolute top-1 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow-lg border border-gray-200">
                <span className="text-xs font-bold text-gray-800">N</span>
              </div>
              <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow-lg border border-gray-200">
                <span className="text-xs font-bold text-gray-800">S</span>
              </div>
              <div className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow-lg border border-gray-200">
                <span className="text-xs font-bold text-gray-800">W</span>
              </div>
              <div className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow-lg border border-gray-200">
                <span className="text-xs font-bold text-gray-800">E</span>
              </div>
            </div>
          </div>
        )}

        {/* Events Drill-Down View */}
        {drillMode === 'events' && selectedIncident && (
          <div className="relative z-10 p-6 space-y-4">
            <div className="text-center mb-6">
              <Badge className={`${getSeverityColor(selectedIncident.severity)} px-4 py-2 text-sm font-semibold`}>
                {selectedIncident.name} • {selectedIncident.count} incidents
              </Badge>
            </div>

            <div className="grid gap-3">
              {selectedIncident.events.map((event, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-4 cursor-pointer hover:bg-white/90 hover:shadow-lg transition-all duration-300 border border-white/50"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full shadow-md ${event.severity === 'High' ? 'bg-red-500' :
                          event.severity === 'Medium' ? 'bg-orange-500' : 'bg-yellow-500'
                        }`}></div>
                      <span className="text-gray-800 font-semibold">{event.name}</span>
                    </div>
                    <Badge className="bg-blue-600 text-white shadow-sm">{event.count}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Triggers Drill-Down View */}
        {drillMode === 'triggers' && selectedEvent && (
          <div className="relative z-10 p-6 space-y-4">
            <div className="text-center mb-6">
              <Badge className={`${getSeverityColor(selectedEvent.severity)} px-4 py-2 text-sm font-semibold`}>
                {selectedEvent.name} Triggers
              </Badge>
            </div>

            <div className="grid gap-3">
              {selectedEvent.triggers.map((trigger, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full shadow-sm ${selectedEvent.severity === 'High' ? 'bg-red-400' :
                        selectedEvent.severity === 'Medium' ? 'bg-orange-400' : 'bg-yellow-400'
                      }`}></div>
                    <span className="text-gray-800 text-sm font-medium">{trigger}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Widget Modal Component
  const WidgetModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowWidgetModal(false)}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Manage Dashboard Widgets</h3>
          <Button variant="ghost" size="sm" onClick={() => setShowWidgetModal(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-3">
          {availableWidgets.map((widget, index) => (
            <div key={widget.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <div className="flex flex-col mr-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1"
                  disabled={index === 0}
                  onClick={() => moveWidget(widget.id, -1)}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1"
                  disabled={index === availableWidgets.length - 1}
                  onClick={() => moveWidget(widget.id, 1)}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">{widget.name}</h4>
                <p className="text-xs text-gray-500 mt-1">{widget.description}</p>
              </div>
              <Button
                size="sm"
                variant={widget.enabled ? "default" : "outline"}
                onClick={() => toggleWidget(widget.id)}
                className={`ml-3 ${widget.enabled ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                {widget.enabled ? (
                  <>
                    <Eye className="h-3 w-3 mr-1" />
                    Remove
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-6">
          <Button onClick={() => setShowWidgetModal(false)}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );


  const recentIncidentsHistory = async (incident) => {
    setLoading(true);
    const recentIncident = {
      id: incident.id,
      type: incident.incidentType,
      severity: incident.level,
      shortLocationLabel: incident.location,
      fullLocation: incident.location,
    }
    try {
      const res = await CommanServices.postIncidentHistory(recentIncident);
      if (res?.status) {
        dispatch(setRecentIncidentHistory(res?.data))
      }
    } catch (err) {
      console.error('Failed to load Post Incident Reports', err);
    } finally {
      setLoading(false);
    }
    dispatch(setRecentIncidentHistory({ ...recentIncident, openedAt: new Date().toISOString() }))
  }

  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);

  /**
   * Export the currently filtered incidents (not paginated) to an Excel file.
   * - Uses global window.XLSX (if included via CDN) or dynamically imports 'xlsx' (if installed).
   * - Falls back to a CSV download if neither is available.
   * - Disables the button and shows a short spinner while preparing the file.
   */
  const exportIncidentsToExcel = async () => {
    try {
      setExportError(null);
      setExportLoading(true);

      // Use filteredIncidents (all filtered rows, not just the paginated page)
      const data = filteredIncidents.map((inc, idx) => ({
        Serial: idx + 1,
        'Incident ID': inc.id ? `INC-${String(inc.id).slice(-8).toUpperCase()}` : '',
        'Date & Time': (inc.detected_at || inc.created_at) ? parseTimestamp(inc.detected_at || inc.created_at).toLocaleString('en-US', { hour12: false }) : '',
        'Incident Type': getIncidentTypeName(inc.type_id),
        Event: getEventName(inc.type_id, inc.event_id),
        Trigger: getTriggerName(inc.type_id, inc.event_id, inc.trigger_id),
        Location: getLocationDisplay(inc),
        Duration: calculateIncidentDuration(inc),
        Source: getIncidentSource(inc),
        Level: inc.resolved_severity || inc.severity || '',
        Status: inc.status || ''
      }));

      if (!data || data.length === 0) {
        setExportError('No incidents to export.');
        return;
      }

      // Try to get XLSX (SheetJS) - prefer global (CDN) then dynamic import
      let XLSX = null;
      if (typeof window !== 'undefined' && window.XLSX) {
        XLSX = window.XLSX;
      } else {
        try {
          const mod = await import('xlsx');
          XLSX = mod;
        } catch (err) {
          XLSX = null;
        }
      }

      const filenameBase = `incident_summary_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}`;

      if (XLSX && XLSX.utils && XLSX.utils.json_to_sheet) {
        // Create worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Incidents');

        // Write workbook to array buffer and trigger download
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filenameBase}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        // Fallback to CSV (Excel can open CSV)
        const headers = Object.keys(data[0]);
        const csvRows = [
          headers.join(','),
          ...data.map(row => headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(','))
        ];
        const csv = csvRows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filenameBase}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export failed', err);
      setExportError('Failed to generate export. Please try again.');
    } finally {
      // Keep the loading spinner visible for at least 600ms to avoid flicker
      await new Promise(res => setTimeout(res, 600));
      setExportLoading(false);
    }
  };

  return (
    <div className='p-6 flex-1 overflow-y-auto max-h-[calc(100vh-81px)]'>
      {isAdmin && showWidgetModal && <WidgetModal />}
      {showFilterModal && (
        <FilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          incidents={incidents}
          incidentClassifications={incidentClassifications}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
        />
      )}

      {/* Main Content */}
      {/* Scrollable Content */}
      {/* CSS Animation for marquee with hover pause */}
      <style>{`
            @keyframes marquee {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
            .animate-marquee {
              animation: marquee 45s linear infinite;
              will-change: transform;
              backface-visibility: hidden;
              perspective: 1000px;
            }
            .animate-marquee:hover {
              animation-play-state: paused;
            }
            .pause-marquee {
              animation-play-state: paused;
            }
          `}</style>
      {/* TV-Style Moving News Ticker with Hover Pause + Add Widget button at the same level */}
      <div className="mb-8 flex items-stretch gap-3">
      <div className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg shadow-lg overflow-hidden relative">
        <div className="flex items-center">
          <div className="bg-white text-red-600 px-3 py-1 rounded-full text-xs font-bold mr-4 flex-shrink-0">
            LIVE
          </div>
          <div className="overflow-hidden flex-1">
            <div className="animate-marquee hover:pause-marquee flex space-x-8 whitespace-nowrap">
              {liveIncidents.concat(liveIncidents).map((incident, index) => {
                // Determine severity dot color based on incident severity
                const getSeverityDot = (severity) => {
                  switch (severity?.toLowerCase()) {
                    case "high":
                    case "critical":
                      return "bg-white border-2 border-red-100"; // High severity - white on red background
                    case "medium":
                      return "bg-orange-300 border border-orange-100"; // Medium severity - orange
                    case "low":
                      return "bg-yellow-300 border border-yellow-100"; // Low severity - yellow
                    default:
                      return "bg-orange-300 border border-orange-100"; // Default medium
                  }
                };

                // Format display text based on incident type
                const displayText = incident.id === 'no-incidents'
                  ? incident.location // Show "All Systems Operational" 
                  : `${incident.name} - ${incident.location} (${incident.startsIn || incident.timeAgo})`;

                return (
                  <div
                    key={`${incident.id}-${index}`}
                    className="flex items-center space-x-3 text-sm font-medium cursor-pointer hover:bg-red-700 hover:bg-opacity-80 hover:rounded px-3 py-1 transition-all duration-200"
                    title={`Click to view details | Status: ${incident.status} | Severity: ${incident.severity}`}
                    onClick={() => {
                      if (incident.id !== 'no-incidents') {
                        navigate(`/incident-details/${incident.id}`);
                      }
                    }}
                  >
                    {/* Severity Level Dot - Restored for Live Line */}
                    <div className={`w-3 h-3 rounded-full ${getSeverityDot(incident.severity)} animate-pulse shadow-sm`}></div>
                    <span className="whitespace-nowrap">{displayText}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {isAdmin && (
        <Button
          className="h-auto self-stretch rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg flex-shrink-0"
          onClick={() => setShowWidgetModal(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Widget
        </Button>
      )}
      </div>

      {/* Dynamic Widgets Section */}
      <div className="mb-8">

        {enabledWidgets.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {enabledWidgets.map((widget) => (
              <div key={widget.id} className={['drill-metrics', 'incident-metrics', 'geo-analytics'].includes(widget.id) ? 'lg:col-span-2' : ''}>
                {widget.id === 'drill-metrics' && (
                  <DashboardMetricsSection
                    title="Simulation & Drill Metrics"
                    subtitle="Exercise readiness & corrective-action overview"
                    icon={Dumbbell}
                    iconGradient="from-blue-600 to-purple-600"
                    metrics={keyMetrics}
                  />
                )}
                {widget.id === 'incident-metrics' && (
                  <DashboardMetricsSection
                    title="Incident Metrics"
                    subtitle="Real-incident severity & status overview"
                    icon={Flame}
                    iconGradient="from-red-500 to-orange-500"
                    metrics={incidentKeyMetrics}
                  />
                )}
                {widget.id === 'geo-analytics' && <DashboardGeoAnalyticsWidget />}
                {widget.id === 'incident-trend' && <DashboardIncidentTrendWidget />}
                {widget.id === 'ai-insights' && <DashboardAIInsightsWidget />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Incident Summary Table */}
      <Card className="shadow-2xl bg-white border-0 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-gray-900 to-blue-900 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-white">
                  {(filters.reportKind || 'all') === 'real' ? 'Incident Summary'
                    : (filters.reportKind || 'all') === 'drill' ? 'Drill & Exercise Summary'
                    : 'Incident & Drill Summary'}
                </CardTitle>
                <p className="text-gray-200 text-sm">
                  {(filters.reportKind || 'all') === 'real' ? 'Live incident monitoring'
                    : (filters.reportKind || 'all') === 'drill' ? 'Live simulation monitoring'
                    : 'Live incident & simulation monitoring'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 ml-2"
                onClick={() => setIsIncidentSummaryCollapsed(!isIncidentSummaryCollapsed)}
              >
                {isIncidentSummaryCollapsed ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronUp className="h-5 w-5" />
                )}
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="border-white/30 text-white hover:bg-white/10 relative"
                onClick={() => setShowFilterModal(true)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filter
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => setShowColumnSelector(!showColumnSelector)}
              >
                <Eye className="h-4 w-4 mr-2" />
                Show/Hide
              </Button>
              {isAdmin && <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={exportIncidentsToExcel}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </>
                  )}
                </Button>
                {exportError && (
                  <div className="ml-3 text-sm text-red-200">
                    {exportError}
                  </div>
                )}
              </div>}
            </div>
          </div>
        </CardHeader>

        {/* Column Selector Dropdown */}
        {showColumnSelector && (
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Show/Hide Columns</h3>
              <button
                onClick={() => setShowColumnSelector(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'serial', label: 'Serial' },
                { key: 'incidentId', label: 'Incident ID' },
                { key: 'dateTime', label: 'Date & Time' },
                { key: 'incidentType', label: 'Incident Type' },
                { key: 'exerciseType', label: 'Exercise Type' },
                { key: 'event', label: 'Event' },
                { key: 'trigger', label: 'Trigger' },
                { key: 'location', label: 'Location' },
                { key: 'department', label: 'Department' },
                { key: 'duration', label: 'Duration' },
                { key: 'level', label: 'Level' },
                { key: 'status', label: 'Status' }
              ].map(column => (
                <label key={column.key} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={(e) => setVisibleColumns({
                      ...visibleColumns,
                      [column.key]: e.target.checked
                    })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{column.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={`transition-all duration-300 ease-in-out ${isIncidentSummaryCollapsed ? 'hidden' : 'block'}`}>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-100 to-gray-200 border-b-2 border-gray-300">
                    {visibleColumns.serial && <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Serial</th>}
                    {visibleColumns.incidentId && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Incident ID</th>}
                    {visibleColumns.dateTime && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date & Time</th>}
                    {visibleColumns.incidentType && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Incident Type</th>}
                    {visibleColumns.exerciseType && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Exercise Type</th>}
                    {visibleColumns.event && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Event</th>}
                    {visibleColumns.trigger && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Trigger</th>}
                    {visibleColumns.location && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>}
                    {visibleColumns.department && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department</th>}
                    {visibleColumns.duration && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Duration</th>}
                    {visibleColumns.level && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Level</th>}
                    {visibleColumns.status && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {processedIncidents.map((incident, index) => (
                    <tr
                      key={incident.id || index}
                      className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 border-l-4 border-l-transparent hover:border-l-blue-400 cursor-pointer"
                      onClick={() => {
                        recentIncidentsHistory(incident);
                        navigate(`/incident-details/${incident.id}`);
                      }}
                    >
                      {visibleColumns.serial && (
                        <td className="px-4 py-5 text-center">
                          <span className="text-sm text-gray-600 font-medium">
                            {filteredIncidents.length - (startIndex + index)}
                          </span>
                        </td>
                      )}
                      {visibleColumns.incidentId && (
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            {/* Tree structure visualization when hierarchy is active */}
                            {incident._treeDepth !== undefined && (filters.incidentFamily || activeFilterCount > 0) && (
                              <span className="text-gray-400 font-mono text-sm flex-shrink-0">
                                {incident._treeDepth === 0 ? (
                                  // Root node
                                  <span className="inline-flex items-center">
                                    <span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
                                  </span>
                                ) : (
                                  // Child nodes with connecting lines
                                  <span className="inline-flex items-center">
                                    {/* Indentation */}
                                    <span style={{ width: `${incident._treeDepth * 12}px` }} className="inline-block"></span>
                                    {/* Check if this is the last child - for now assume it's not (would need sibling info) */}
                                    <span className="mr-1">
                                      {incident._treeDepth === 1 ? '├─' : incident._treeDepth === 2 ? '└─' : '├─'}
                                    </span>
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                  </span>
                                )}
                              </span>
                            )}

                            {/* Show Related chip only when NOT in tree hierarchy mode */}
                            {incident._treeDepth === undefined && incident.parent_incident_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/incident-details/${incident.parent_incident_id}`);
                                }}
                                className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                                title={`Navigate to parent incident ${incident.parent_incident_id}`}
                              >
                                <Link2 className="h-4 w-4" />
                              </button>
                            )}

                            <span className="text-sm text-gray-900 font-semibold">
                              {incident.id ? `INC-${incident.id.slice(-8).toUpperCase()}` : 'N/A'}
                            </span>

                            {/* Real vs Drill marker (shown when the table mixes both kinds) */}
                            {(filters.reportKind || 'all') === 'all' && (
                              incident.is_exercise ? (
                                <span className="text-[9px] font-extrabold uppercase tracking-tight px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Drill</span>
                              ) : (
                                <span className="text-[9px] font-extrabold uppercase tracking-tight px-1.5 py-0.5 rounded bg-red-100 text-red-700">Real</span>
                              )
                            )}

                            {/* Live Call Indicator */}
                            {activeCalls[incident.id] && (
                              <div 
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 animate-pulse cursor-pointer hover:bg-emerald-100 transition-all group/call shadow-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/incident-details/${incident.id}/chat`);
                                }}
                                title="Active call in progress. Tap to join."
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shadow-[0_0_8px_rgba(16,185,129,0.6)]"></div>
                                <Phone className="h-3 w-3 text-emerald-600 fill-emerald-600/10" />
                                <span className="text-[9px] font-extrabold text-emerald-600 uppercase tracking-tighter">Live</span>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.dateTime && (
                        <td className="px-6 py-5">
                          {(() => {
                            const dateValue = (incident.is_exercise && incident.planned_start_at)
                              ? incident.planned_start_at
                              : (incident.detected_at || incident.created_at);
                            return (
                              <span className="text-sm text-gray-900 font-medium">
                                {dateValue
                                  ? parseTimestamp(dateValue).toLocaleString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                  : 'Not available'
                                }
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      {visibleColumns.incidentType && (
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-semibold text-red-600">
                              {incident.incidentType}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.exerciseType && (
                        <td className="px-6 py-5">
                          {incident.is_exercise && incident.exerciseType && incident.exerciseType !== '—' ? (
                            <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 font-medium" data-testid="exercise-type-badge">
                              {incident.exerciseType}
                            </Badge>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.event && (
                        <td className="px-6 py-5">
                          <span className="text-sm text-gray-900 font-medium">
                            {incident.event}
                          </span>
                        </td>
                      )}
                      {visibleColumns.trigger && (
                        <td className="px-6 py-5">
                          <span className="text-sm text-gray-900 font-medium">
                            {incident.trigger}
                          </span>
                        </td>
                      )}
                      {visibleColumns.location && (
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-2">
                            <MapPin
                              className={`h-4 w-4 ${incident.locationVerified ? 'text-emerald-500' : 'text-gray-400'}`}
                              aria-label={incident.locationVerified ? 'Verified location' : 'Unverified location'}
                            />
                            <span className="text-sm text-gray-900 font-medium">
                              {incident.location}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.department && (
                        <td className="px-6 py-5">
                          <span className="text-sm text-gray-900">
                            {incident.department || '—'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.duration && (
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-900 font-mono">{incident.duration}</span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.level && (
                        <td className="px-6 py-5">
                          <Badge className={`${incident.levelColor} border-0 font-medium shadow-sm`}>
                            {incident.level}
                          </Badge>
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="px-6 py-5">
                          <Badge className={`${incident.statusColor} text-white hover:${incident.statusColor} font-medium px-3 py-1 shadow-md`}>
                            {incident.status}
                          </Badge>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Real Pagination - 50 incidents per page */}
            <div className="flex items-center justify-between p-6 bg-gradient-to-r from-gray-50 to-gray-100 border-t">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  Live Data
                </div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-bold text-blue-600">{startIndex + 1}</span>-<span className="font-bold text-blue-600">{Math.min(endIndex, filteredIncidents.length)}</span> of <span className="font-bold text-blue-600">{filteredIncidents.length}</span> incidents
                  {activeFilterCount > 0 && (
                    <span className="text-blue-600 font-medium ml-1">(filtered)</span>
                  )}
                  <span className="text-gray-500 ml-2">• {itemsPerPage} per page</span>
                </p>
              </div>

              {/* Real Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className={currentPage === 1 ? "opacity-50" : "hover:bg-blue-50"}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={pageNum === currentPage ? "default" : "outline"}
                        onClick={() => setCurrentPage(pageNum)}
                        className={pageNum === currentPage
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                          : "hover:bg-blue-50"
                        }
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  {/* Show ellipsis and last page if needed */}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="px-2 text-gray-400">...</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(totalPages)}
                        className="hover:bg-blue-50"
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className={currentPage === totalPages ? "opacity-50" : "hover:bg-blue-50"}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </div>
      </Card>
    </div>
  );
};
export default Dashboard;
